<template>
  <v-container class="ma-0 pa-0" fluid>
    <div class="d-flex align-stretch">
      <div class="flex-grow-1" style="min-width:0;">
    <v-data-table
      :headers="displayHeaders"
      :height="tableSize"
      :items="filteredItems"
      :items-per-page="pageSize"
      :loading="loading"
      :search="search"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      dense
      fixed-header
      item-key="ad_plan_id"
      loading-text="Loading... Please Wait"
      no-results-text="No matching items found"
      :no-data-text="selectedAdGroup ? 'No current or future ad planner items found' : 'Select an ad group to view ad planner items'">
      <template #top>
        <v-toolbar flat color="toolbar" height="auto" class="py-2" style="cursor:default;">
          <v-row dense align="center">
            <v-col cols="12" class="d-flex flex-wrap align-center">
              <v-toolbar-title>Ad Planner Search</v-toolbar-title>
              <v-divider vertical inset class="mx-4" />
              <span class="text-caption grey--text">
                {{ items.length.toLocaleString() }} items across {{ plannerCount }} current/future planners
              </span>
              <v-chip v-if="loadWarning" small color="amber" text-color="white" class="ml-3">
                <v-icon left small>mdi-alert-outline</v-icon>{{ loadWarning }}
              </v-chip>
            </v-col>
            <v-col md="3" lg="2">
              <v-autocomplete
                auto-select-first
                v-model="selectedAdGroup"
                :items="userAdGroups"
                item-text="name"
                item-value="id"
                return-object
                hide-no-data
                background-color="input"
                label="Ad Group"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-filter-outline"
                :menu-props="{ maxHeight: 260 }" />
            </v-col>
            <v-col md="4" lg="3">
              <v-text-field
                v-model="search"
                background-color="input"
                label="Search"
                placeholder="Search all columns…"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify" />
            </v-col>
            <v-col cols="12" md="5" lg="7" class="d-flex flex-wrap align-center">
              <v-btn
                outlined
                color="primary"
                class="mr-2"
                @click="filterDrawer = true">
                <v-icon left>mdi-filter-variant</v-icon>
                Filters
                <v-chip v-if="activeFilterCount" x-small color="primary" class="ml-2 white--text">{{ activeFilterCount }}</v-chip>
              </v-btn>
              <v-btn outlined class="mr-2" @click="openColumnsModal">
                <v-icon left>mdi-table-edit</v-icon>
                Columns
              </v-btn>
              <v-menu offset-y :close-on-content-click="false" max-width="320">
                <template #activator="{ on: menuOn, attrs }">
                  <v-btn v-bind="attrs" v-on="menuOn" outlined class="mr-2">
                    <v-icon left>mdi-content-save-cog-outline</v-icon>
                    Saved Views
                  </v-btn>
                </template>
                <v-list dense>
                  <v-subheader>Saved Views</v-subheader>
                  <v-list-item v-if="!savedViews.length" disabled>
                    <v-list-item-content>
                      <v-list-item-title class="grey--text">No saved views yet</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-for="view in savedViews" :key="view.id" @click="applyView(view)">
                    <v-list-item-content>
                      <v-list-item-title>{{ view.name }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon small @click.stop="deleteView(view.id)">
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider />
                  <v-list-item @click="openSaveViewDialog">
                    <v-list-item-icon class="mr-2">
                      <v-icon>mdi-plus</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Save current view…</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-spacer />
              <v-btn outlined :disabled="!filteredItems.length" @click="exportCsv">
                <v-icon left>mdi-download</v-icon>CSV
              </v-btn>
            </v-col>
          </v-row>
        </v-toolbar>
      </template>

      <template #[`item.ad_start_date`]="{ item }">
        {{ formatDateDisplay(item.ad_start_date) }}
      </template>
      <template #[`item.ad_end_date`]="{ item }">
        {{ formatDateDisplay(item.ad_end_date) }}
      </template>
    </v-data-table>
      </div>
      <v-expand-x-transition>
        <div
          v-show="filterDrawer"
          class="flex-shrink-0"
          style="width:340px; border-left:1px solid rgba(0,0,0,0.12);">
          <AdPlannerSearchFilters
            v-model="filters"
            @close="filterDrawer = false"
            :position-options="positionOptions"
            :department-options="departmentOptions"
            :promo-category-options="promoCategoryOptions"
            :vendor-options="vendorOptions"
            :category-options="categoryOptions"
            :subcategory-options="subcategoryOptions"
            :epay-compete-options="epayCompeteOptions" />
        </div>
      </v-expand-x-transition>
    </div>

    <!-- D5.3: PricePro-style "Ordering Headers" reorder/hide/save -->
    <v-dialog v-model="showColumnsModal" max-width="600px" scrollable persistent>
      <v-card>
        <v-card-title class="accent white--text pa-3 justify-center">Ordering Headers</v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="6">
              <h3 class="text-center mb-2">Display Order</h3>
              <draggable class="list-group" :list="displayHeadersDraft" group="headers" @end="onDragEnd">
                <div v-for="header in displayHeadersDraft" :key="header.value" class="list-group-item pa-2 my-1">
                  {{ header.text }}
                </div>
              </draggable>
            </v-col>
            <v-col cols="6">
              <h3 class="text-center mb-2">Hide</h3>
              <draggable class="list-group" :list="hideHeadersDraft" group="headers" @end="onDragEnd">
                <div v-for="header in hideHeadersDraft" :key="header.value" class="list-group-item pa-2 my-1">
                  {{ header.text }}
                </div>
              </draggable>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="grey lighten-4">
          <v-spacer />
          <v-btn color="accent white--text" @click="resetHeaders">Reset</v-btn>
          <v-btn color="green darken-1 white--text" @click="saveHeadersOrder">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Saved Views: name the current {columns, filters, sort} and store locally -->
    <v-dialog v-model="showSaveViewDialog" max-width="420px" persistent>
      <v-card>
        <v-card-title class="accent white--text pa-3">Save Current View</v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="newViewName"
            label="View name"
            outlined
            dense
            autofocus
            hide-details
            @keyup.enter="saveCurrentView" />
        </v-card-text>
        <v-divider />
        <v-card-actions class="grey lighten-4">
          <v-spacer />
          <v-btn text @click="showSaveViewDialog = false">Cancel</v-btn>
          <v-btn color="green darken-1 white--text" :disabled="!newViewName.trim()" @click="saveCurrentView">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// api
import AdRetails from '@/axios/ad-retails'
// mixins
import { displayAlert } from '@/mixins/alert'
import { fullwidth } from '@/mixins/fullwidth'
import { paging } from '@/mixins/paging'
import { utils } from '@/mixins/utils'
import { displayHelpers } from '@/mixins/displayHelpers'
import { userAccess } from '@/mixins/user-access'
// third party
import draggable from 'vuedraggable'
import moment from 'moment'
import AdPlannerSearchFilters from '@/components/AdPlannerSearchFilters.vue'

const STORAGE_KEY = 'adPlannerQuery_userHeaders'
const SAVED_VIEWS_KEY = 'adPlannerQuery_savedViews'
const ITEM_PAGE_LIMIT = 200
const PLANNER_PAGE_CAP = 15 // max /ad_planners pages per workflow-state combo
const ITEM_PAGE_CAP = 25 // max item pages per planner
const PLANNER_FETCH_CONCURRENCY = 4 // planners fetched in parallel at a time

// "Promo Category" is a promotion scan-allowance type (per PricePro's catToCol
// mapping), derived from which scan-allowance columns are populated on an item.
// The ad-plan item endpoint doesn't expose promo_category_name directly.
// Fresh default-filters object, used both for the initial state and as the base
// that an applied saved view is merged over (so a stale view can't break filters).
const defaultFilters = () => ({ positions: [], departments: [], promoCategories: [], vendors: [], categories: [], subcategories: [], features: '', itemIds: '', epayCompetes: 'all' })

const PROMO_CATEGORY_MAP = [
  { field: 'epay_tpr_scan', label: 'TPR Scan - ePay' },
  { field: 'tpr_scan_compete', label: 'TPR Scan - Compete' },
  { field: 'epay_ad_scan', label: 'Ad Scan - ePay' },
  { field: 'ad_scan_compete', label: 'Ad Scan - Compete' },
  { field: 'epay_edlc_scan', label: 'EDLC Scan - ePay' },
  { field: 'edlc_scan_compete', label: 'EDLC Scan - Compete' }
]

export default {
  name: 'AdPlannerQuery',
  components: { draggable, AdPlannerSearchFilters },
  mixins: [displayAlert, fullwidth, displayHelpers, paging, userAccess, utils],
  data () {
    return {
      items: [],
      plannerCount: 0,
      loadWarning: '',
      selectedAdGroup: null,
      loading: false,
      search: '',
      sortBy: '',
      sortDesc: false,
      filterDrawer: false,
      filters: defaultFilters(),
      // Device-local named views {id, name, columns, filters, sort} per user email.
      savedViews: [],
      showSaveViewDialog: false,
      newViewName: '',
      epayCompeteOptions: [
        { text: 'All', value: 'all' },
        { text: 'Has ePay', value: 'epay' },
        { text: 'Has Competes', value: 'compete' }
      ],
      showColumnsModal: false,
      selectedHeaders: [],
      displayHeadersDraft: [],
      hideHeadersDraft: [],
      // Item-level columns (rows = ad_plan items across current/future planners).
      // Columns mirror the canonical Ad Planner export (G&W SPG CSV): all 40
      // columns. Most map 1:1 to AdPlannerItem fields; TPR DATES is a computed
      // start-end range; SUBVENDOR comes straight from the endpoint (vendor-matched
      // to the item on the server).
      allHeaders: [
        { text: 'SEQ', value: 'position_display_order', align: 'end', class: 'accent white--text' },
        { text: 'AD NAME', value: 'ad_name', class: 'accent white--text' },
        { text: 'ORDER #', value: 'item_id', class: 'accent white--text' },
        { text: 'PAGE NAME', value: 'page_name', class: 'accent white--text' },
        { text: 'POSITION', value: 'position_name', class: 'accent white--text' },
        { text: 'DEPT', value: 'department', class: 'accent white--text' },
        { text: 'FEATURE DESCRIPTION', value: 'feature_text', class: 'accent white--text' },
        { text: 'UPC', value: 'upc', class: 'accent white--text' },
        { text: 'ITEM DESC', value: 'item_desc', class: 'accent white--text' },
        { text: 'PK', value: 'qty_per_case', align: 'end', class: 'accent white--text' },
        { text: 'SZ', value: 'pack_size', class: 'accent white--text' },
        { text: 'COST', value: 'case_cost', align: 'end', class: 'accent white--text' },
        { text: 'COST +FRT', value: 'net_case_cost', align: 'end', class: 'accent white--text' },
        { text: 'AMAP', value: 'amap', align: 'end', class: 'accent white--text' },
        { text: 'EBA', value: 'early_buy_billback', align: 'end', class: 'accent white--text' },
        { text: 'DSD OI', value: 'dsd_oi_billback', align: 'end', class: 'accent white--text' },
        { text: 'EDLC BB', value: 'edlc_billback', align: 'end', class: 'accent white--text' },
        { text: 'TPR BB', value: 'tpr_billback', align: 'end', class: 'accent white--text' },
        { text: 'AD BB', value: 'ad_billback', align: 'end', class: 'accent white--text' },
        { text: 'EDLC SCAN', value: 'edlc_scan', align: 'end', class: 'accent white--text' },
        { text: 'EDLC EPAY', value: 'epay_edlc_scan', align: 'end', class: 'accent white--text' },
        { text: 'EDLC COMPETE', value: 'edlc_scan_compete', align: 'end', class: 'accent white--text' },
        { text: 'TPR SCAN', value: 'tpr_scan', align: 'end', class: 'accent white--text' },
        { text: 'TPR EPAY', value: 'epay_tpr_scan', align: 'end', class: 'accent white--text' },
        { text: 'TPR COMPETE', value: 'tpr_scan_compete', align: 'end', class: 'accent white--text' },
        { text: 'AD SCAN', value: 'ad_scan', align: 'end', class: 'accent white--text' },
        { text: 'AD SCAN EPAY', value: 'epay_ad_scan', align: 'end', class: 'accent white--text' },
        { text: 'COMPLEX REDEMPTION', value: 'complex_scan', align: 'end', class: 'accent white--text' },
        { text: 'NET UNIT COST', value: 'net_unit_cost', align: 'end', class: 'accent white--text' },
        { text: 'NUC - COMPLEX', value: 'complex_net_unit_cost', align: 'end', class: 'accent white--text' },
        { text: 'PER CASE AD FEE', value: 'pcaf_billback', align: 'end', class: 'accent white--text' },
        { text: 'COUNT', value: 'ad_retail_count', align: 'end', class: 'accent white--text' },
        { text: 'AD SRP', value: 'ad_retail', align: 'end', class: 'accent white--text' },
        { text: 'GP%', value: 'ad_gross_profit_percent', align: 'end', class: 'accent white--text' },
        { text: 'REGSRP', value: 'regular_retail', align: 'end', class: 'accent white--text' },
        { text: 'MVMT', value: 'last_90_days_shipped_qty', align: 'end', class: 'accent white--text' },
        { text: 'SUBVENDOR', value: 'subvendor', class: 'accent white--text' },
        { text: 'TPR RETAIL', value: 'tpr_retail', align: 'end', class: 'accent white--text' },
        { text: 'TPR DATES', value: 'tpr_dates', align: 'center', class: 'accent white--text' },
        { text: 'FEATURE NOTES', value: 'feature_notes', class: 'accent white--text' }
      ]
    }
  },
  beforeCreate () {
    this.$store.dispatch('getAdGroups')
  },
  created () {
    this.nonTableHeight = 260
    this.loadUserHeaders()
    this.loadSavedViews()
    if (this.userAdGroups.length > 0 && !this.selectedAdGroup) {
      this.selectedAdGroup = this.userAdGroups[0]
    }
  },
  watch: {
    selectedAdGroup: {
      handler (newValue) {
        if (newValue) this.getItems()
      },
      deep: true
    },
    userAdGroups: {
      handler (newValue) {
        if (newValue?.length > 0 && !this.selectedAdGroup) {
          this.selectedAdGroup = newValue[0]
        }
      },
      deep: true
    }
  },
  computed: {
    userEmail () {
      return this.$store.getters.email || ''
    },
    adGroups () {
      return this.$store.getters.adGroups
    },
    userAdGroups () {
      if (this.adGroups.length > 0) {
        let groups = this.adGroups
        if (this.$auth.tenant === 'alliance-retail-group') {
          groups = groups.filter(g => g?.attributes?.AD_GROUP_IS_ACTIVE === 'True')
        }
        return groups.slice().sort(this.sortByName)
      }
      return []
    },
    displayHeaders () {
      return this.selectedHeaders
        .map(v => this.allHeaders.find(h => h.value === v))
        .filter(Boolean)
    },
    positionOptions () { return this.distinctValues('position_name') },
    departmentOptions () { return this.distinctValues('department') },
    vendorOptions () { return this.distinctValues('vendor') },
    categoryOptions () { return this.distinctValues('category') },
    subcategoryOptions () { return this.distinctValues('subcategory') },
    promoCategoryOptions () {
      const present = new Set()
      for (const it of this.items) {
        for (const c of this.itemPromoCategories(it)) present.add(c)
      }
      return PROMO_CATEGORY_MAP.map(m => m.label).filter(l => present.has(l))
    },
    filtersActive () {
      const f = this.filters
      return f.positions.length > 0 || f.departments.length > 0 || f.promoCategories.length > 0 ||
        f.vendors.length > 0 || f.categories.length > 0 || f.subcategories.length > 0 ||
        (f.features || '').trim() !== '' || (f.itemIds || '').trim() !== '' || f.epayCompetes !== 'all'
    },
    activeFilterCount () {
      const f = this.filters
      return f.positions.length + f.departments.length + f.promoCategories.length +
        f.vendors.length + f.categories.length + f.subcategories.length +
        ((f.features || '').trim() !== '' ? 1 : 0) + ((f.itemIds || '').trim() !== '' ? 1 : 0) +
        (f.epayCompetes !== 'all' ? 1 : 0)
    },
    filteredItems () {
      const f = this.filters
      if (!this.filtersActive) return this.items
      const featureTerm = (f.features || '').trim().toLowerCase()
      const itemIdTerm = (f.itemIds || '').trim().toLowerCase()
      return this.items.filter(it => {
        if (f.positions.length && !f.positions.includes(it.position_name)) return false
        if (f.departments.length && !f.departments.includes(it.department)) return false
        if (f.promoCategories.length) {
          const cats = this.itemPromoCategories(it)
          if (!f.promoCategories.some(c => cats.includes(c))) return false
        }
        if (f.vendors.length && !f.vendors.includes(it.vendor)) return false
        if (f.categories.length && !f.categories.includes(it.category)) return false
        if (f.subcategories.length && !f.subcategories.includes(it.subcategory)) return false
        if (featureTerm && !String(it.feature_text ?? '').toLowerCase().includes(featureTerm)) return false
        if (itemIdTerm && !String(it.item_id ?? '').toLowerCase().includes(itemIdTerm)) return false
        if (f.epayCompetes === 'epay' && !this.hasEpay(it)) return false
        if (f.epayCompetes === 'compete' && !this.hasCompete(it)) return false
        return true
      })
    }
  },
  methods: {
    // Build the item-level grid by aggregating items across all current/future
    // planners for the selected ad group (no bulk endpoint exists, so we fan out
    // per ad_id and concatenate client-side).
    async getItems () {
      const partyId = this.selectedAdGroup?.id
      if (!partyId) {
        this.items = []
        this.plannerCount = 0
        return
      }
      this.loading = true
      this.loadWarning = ''
      try {
        const planners = await this.getAllPlanners(partyId)
        const today = moment().format('YYYY-MM-DD')
        const currentFuture = planners.filter(a => (a.ad_end_date || '') >= today)
        this.plannerCount = currentFuture.length

        // Fetch planners' items in small concurrent batches so a group with many
        // planners doesn't fire a large burst of requests at once.
        const collected = []
        for (let i = 0; i < currentFuture.length; i += PLANNER_FETCH_CONCURRENCY) {
          const batch = currentFuture.slice(i, i + PLANNER_FETCH_CONCURRENCY)
          const results = await Promise.all(batch.map(ad => this.getPlannerItems(partyId, ad)))
          for (const r of results) collected.push(...r)
        }
        this.items = collected
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },
    async getAllPlanners (partyId) {
      // /ad_planners filters on grid_submitted + approved (each a required bool),
      // so a single call only returns one workflow state. To "show everything",
      // query all 4 states and merge by ad_id. limit caps at 200 -> page through.
      const combos = [
        { grid_submitted: true, approved: true },
        { grid_submitted: true, approved: false },
        { grid_submitted: false, approved: true },
        { grid_submitted: false, approved: false }
      ]
      const byId = new Map()
      for (const c of combos) {
        let offset = 0
        for (let page = 0; page < PLANNER_PAGE_CAP; page++) {
          let res
          try {
            res = await AdRetails.getAdPlanners(partyId, offset, ITEM_PAGE_LIMIT, null, c.grid_submitted, c.approved)
          } catch (err) {
            this.flagIncomplete('Some ad planners could not be loaded — results may be incomplete.')
            break
          }
          const rows = res?.data || []
          for (const r of rows) {
            if (r.ad_id && !byId.has(r.ad_id)) byId.set(r.ad_id, r)
          }
          if (rows.length < ITEM_PAGE_LIMIT) break
          offset += ITEM_PAGE_LIMIT
          if (page === PLANNER_PAGE_CAP - 1) {
            this.flagIncomplete('Reached the ad-planner page cap — some planners may not be shown.')
          }
        }
      }
      return Array.from(byId.values())
    },
    distinctValues (field) {
      const set = new Set()
      for (const it of this.items) {
        const v = it[field]
        if (v != null && v !== '') set.add(v)
      }
      return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)))
    },
    hasEpay (it) {
      return Number(it.epay_tpr_scan) > 0 || Number(it.epay_ad_scan) > 0 || Number(it.epay_edlc_scan) > 0
    },
    hasCompete (it) {
      return Number(it.tpr_scan_compete) > 0 || Number(it.ad_scan_compete) > 0 || Number(it.edlc_scan_compete) > 0
    },
    itemPromoCategories (it) {
      return PROMO_CATEGORY_MAP.filter(m => Number(it[m.field]) > 0).map(m => m.label)
    },
    clearFilters () {
      this.filters = defaultFilters()
    },
    flagIncomplete (msg) {
      // keep the first warning so the user knows results may be partial/capped
      if (!this.loadWarning) this.loadWarning = msg
    },
    async getPlannerItems (partyId, ad) {
      const collected = []
      let offset = 0
      // page through this planner's items, tagging each with its ad context
      for (let page = 0; page < ITEM_PAGE_CAP; page++) {
        let res
        try {
          res = await AdRetails.getRetailAdItems(ad.ad_id, { party_id: partyId, limit: ITEM_PAGE_LIMIT, offset })
        } catch (err) {
          this.flagIncomplete(`Some items for "${ad.ad_name}" could not be loaded — results may be incomplete.`)
          break
        }
        const rows = res?.data || []
        for (const it of rows) {
          collected.push({
            ...it,
            ad_name: ad.ad_name,
            ad_start_date: ad.ad_start_date,
            ad_end_date: ad.ad_end_date,
            // TPR DATES column: combined start–end range. Render whatever is
            // present (a single boundary still shows) so partial windows aren't hidden.
            tpr_dates: (it.tpr_start_date || it.tpr_end_date)
              ? `${it.tpr_start_date ? moment(it.tpr_start_date).format('MM/DD/YY') : ''}–${it.tpr_end_date ? moment(it.tpr_end_date).format('MM/DD/YY') : ''}`
              : ''
          })
        }
        if (rows.length < ITEM_PAGE_LIMIT) break
        offset += ITEM_PAGE_LIMIT
        if (page === ITEM_PAGE_CAP - 1) {
          this.flagIncomplete('Reached the item page cap — some items may not be shown.')
        }
      }
      return collected
    },
    // -- column reorder/hide/save (per-user localStorage; PricePro pattern) --
    loadUserHeaders () {
      const all = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
      const saved = all[this.userEmail]
      if (Array.isArray(saved) && saved.length) {
        const valid = saved.filter(v => this.allHeaders.some(h => h.value === v))
        this.selectedHeaders = valid.length ? valid : this.allHeaders.map(h => h.value)
      } else {
        this.selectedHeaders = this.allHeaders.map(h => h.value)
      }
    },
    openColumnsModal () {
      this.displayHeadersDraft = this.selectedHeaders
        .map(v => this.allHeaders.find(h => h.value === v))
        .filter(Boolean)
      this.hideHeadersDraft = this.allHeaders.filter(h => !this.selectedHeaders.includes(h.value))
      this.showColumnsModal = true
    },
    onDragEnd () {
      // vuedraggable mutates the draft lists directly; nothing else needed here
    },
    saveHeadersOrder () {
      this.selectedHeaders = this.displayHeadersDraft.map(h => h.value)
      const all = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
      all[this.userEmail] = this.selectedHeaders
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
      this.showColumnsModal = false
    },
    resetHeaders () {
      const all = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
      delete all[this.userEmail]
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
      this.selectedHeaders = this.allHeaders.map(h => h.value)
      this.showColumnsModal = false
    },
    // -- saved views (device-local, per-user; separate from the single-view
    //    column save above). Each view captures {columns, filters, sort}. --
    loadSavedViews () {
      let all = {}
      try {
        all = JSON.parse(localStorage.getItem(SAVED_VIEWS_KEY)) || {}
      } catch (e) {
        all = {}
      }
      const mine = all[this.userEmail]
      this.savedViews = Array.isArray(mine) ? mine : []
    },
    persistSavedViews () {
      let all = {}
      try {
        all = JSON.parse(localStorage.getItem(SAVED_VIEWS_KEY)) || {}
      } catch (e) {
        all = {}
      }
      all[this.userEmail] = this.savedViews
      localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(all))
    },
    openSaveViewDialog () {
      this.newViewName = ''
      this.showSaveViewDialog = true
    },
    saveCurrentView () {
      const name = (this.newViewName || '').trim()
      if (!name) return
      const view = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name,
        columns: this.selectedHeaders.slice(),
        filters: JSON.parse(JSON.stringify(this.filters)),
        sort: { sortBy: this.sortBy, sortDesc: this.sortDesc }
      }
      this.savedViews.push(view)
      this.persistSavedViews()
      this.showSaveViewDialog = false
      this.newViewName = ''
      this.emitAlert(true, 'success', `Saved view "${name}".`)
    },
    applyView (view) {
      if (!view) return
      // Columns: same assignment loadUserHeaders does, keeping only valid values.
      const cols = Array.isArray(view.columns)
        ? view.columns.filter(v => this.allHeaders.some(h => h.value === v))
        : []
      this.selectedHeaders = cols.length ? cols : this.allHeaders.map(h => h.value)
      // Filters: merge over a fresh default so missing/renamed keys can't break.
      this.filters = { ...defaultFilters(), ...(view.filters || {}) }
      // Sort.
      const sort = view.sort || {}
      this.sortBy = sort.sortBy != null ? sort.sortBy : ''
      this.sortDesc = sort.sortDesc != null ? sort.sortDesc : false
    },
    deleteView (id) {
      this.savedViews = this.savedViews.filter(v => v.id !== id)
      this.persistSavedViews()
    },
    // Rows exactly as displayed in the grid: dropdown filters (filteredItems)
    // + the all-columns search box + the current column sort. Mirrors what the
    // v-data-table renders, so the export reflects the user's current view.
    exportViewRows () {
      const headers = this.displayHeaders
      let rows = this.filteredItems
      const term = (this.search || '').trim().toLowerCase()
      if (term) {
        rows = rows.filter(row =>
          headers.some(h => String(row[h.value] ?? '').toLowerCase().includes(term)))
      }
      const key = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy
      const desc = Array.isArray(this.sortDesc) ? this.sortDesc[0] : this.sortDesc
      if (key) {
        rows = [...rows].sort((a, b) => {
          const av = a[key]
          const bv = b[key]
          if (av == null && bv == null) return 0
          if (av == null) return -1
          if (bv == null) return 1
          const an = parseFloat(av)
          const bn = parseFloat(bv)
          const bothNum = !isNaN(an) && !isNaN(bn) &&
            String(av).trim() !== '' && String(bv).trim() !== ''
          const r = bothNum ? an - bn : String(av).localeCompare(String(bv))
          return desc ? -r : r
        })
      }
      return rows
    },
    exportCsv () {
      const headers = this.displayHeaders
      const rows = this.exportViewRows().map(row =>
        headers.map(h => `"${String(row[h.value] ?? '').replace(/"/g, '""')}"`).join(','))
      const csv = [headers.map(h => `"${h.text}"`).join(','), ...rows].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ad-planner-query-${moment().format('YYYY-MM-DD')}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
/* Keep the grid from compressing/wrapping when the filter drawer pushes it:
   the table sizes to its content (at least full width) and scrolls
   horizontally instead of squishing columns into tall wrapped rows. */
::v-deep .v-data-table__wrapper > table {
  width: max-content;
  min-width: 100%;
}
::v-deep .v-data-table__wrapper > table th,
::v-deep .v-data-table__wrapper > table td {
  white-space: nowrap;
}
.list-group {
  min-height: 220px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 6px;
  background: #fafafa;
}
.list-group-item {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: grab;
  user-select: none;
}
</style>
